import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Gift, Copy, Check, Share2, Users, Crown, 
  Trophy, Sparkles, ChevronRight 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { getReferralInfo, applyReferralCode, getReferralLeaderboard } from '../lib/api';

export default function ReferralPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  const [referralInfo, setReferralInfo] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [infoRes, leaderboardRes] = await Promise.all([
        getReferralInfo(),
        getReferralLeaderboard()
      ]);
      setReferralInfo(infoRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (error) {
      console.error('Failed to load referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!referralInfo?.referral_code) return;
    
    try {
      await navigator.clipboard.writeText(referralInfo.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleShare = async () => {
    if (!referralInfo?.share_url) return;
    
    const shareData = {
      title: 'SpenSpot - Gestion finances',
      text: t('share_referral_code').replace('{code}', referralInfo.referral_code),
      url: referralInfo.share_url
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (error) {
      console.error('Share failed:', error);
    }
  };

  const handleApplyCode = async () => {
    if (!codeInput.trim()) return;
    
    setApplyLoading(true);
    setApplyResult(null);
    
    try {
      const response = await applyReferralCode(codeInput.trim());
      setApplyResult({ success: true, message: response.data.message, data: response.data });
      setCodeInput('');
      loadData(); // Refresh data
    } catch (error) {
      setApplyResult({ 
        success: false, 
        message: error.response?.data?.detail || (t('invalid_code'))
      });
    } finally {
      setApplyLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">{t('referral')}</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Hero Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20" data-testid="referral-hero">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{t('referral_title')}</h2>
              <p className="text-muted-foreground mb-6">{t('referral_subtitle')}</p>
              
              {/* Referral Code Display */}
              <div className="bg-card rounded-xl p-4 mb-4">
                <p className="text-sm text-muted-foreground mb-2">{t('your_code')}</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-3xl font-bold tracking-wider text-primary" data-testid="referral-code">
                    {referralInfo?.referral_code || '...'}
                  </span>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={handleCopy}
                    data-testid="copy-code-btn"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Share Button */}
              <Button 
                onClick={handleShare} 
                className="w-full"
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {t('share')}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 gap-4"
        >
          <Card data-testid="friends-invited-card">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-3xl font-bold">{referralInfo?.referral_count || 0}</p>
              <p className="text-sm text-muted-foreground">{t('friends_invited')}</p>
            </CardContent>
          </Card>
          <Card data-testid="premium-earned-card">
            <CardContent className="p-4 text-center">
              <Crown className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
              <p className="text-3xl font-bold">{referralInfo?.premium_months_earned || 0}</p>
              <p className="text-sm text-muted-foreground">{t('premium_earned')}</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Apply Referral Code */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card data-testid="apply-code-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('enter_referral_code')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  placeholder="SPENSPOTXXXXXX"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                  className="flex-1 uppercase"
                  data-testid="referral-code-input"
                />
                <Button 
                  onClick={handleApplyCode}
                  disabled={!codeInput.trim() || applyLoading}
                  data-testid="apply-code-btn"
                >
                  {applyLoading ? (
                    <span className="animate-spin">⏳</span>
                  ) : (
                    t('apply_code')
                  )}
                </Button>
              </div>
              {applyResult && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-3 p-3 rounded-lg text-sm ${
                    applyResult.success 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}
                  data-testid="apply-result"
                >
                  {applyResult.success && <Sparkles className="w-4 h-4 inline mr-2" />}
                  {applyResult.message}
                  {applyResult.data?.referrer_name && (
                    <span className="block mt-1">
                      {t('thanks_to').replace('{name}', applyResult.data.referrer_name)}
                    </span>
                  )}
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Referred Users */}
        {referralInfo?.referred_users?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {t('your_referrals')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {referralInfo.referred_users.map((user, index) => (
                    <div key={index} className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-lg">👤</span>
                        </div>
                        <span className="font-medium">{user.name}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {new Date(user.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card data-testid="leaderboard-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-yellow-500" />
                  {t('leaderboard')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {leaderboard.map((entry, index) => (
                    <div key={index} className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-gray-100 text-gray-700' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {index + 1}
                        </div>
                        <span className="font-medium">{entry.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="font-bold">{entry.referral_count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* How it works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {t('how_it_works')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Step 
                  number={1} 
                  title={language === 'fr' ? 'Partage ton code' : language === 'pt' ? 'Partilha o teu código' : 'Share your code'}
                  description={language === 'fr' 
                    ? 'Envoie ton code unique à tes amis'
                    : 'Send your unique code to friends'
                  }
                />
                <Step 
                  number={2} 
                  title={language === 'fr' ? "Ils s'inscrivent" : language === 'pt' ? 'Eles registam-se' : 'They sign up'}
                  description={language === 'fr' 
                    ? 'Ils créent un compte et entrent ton code'
                    : 'They create an account and enter your code'
                  }
                />
                <Step 
                  number={3} 
                  title={t('you_both_win')}
                  description={language === 'fr' 
                    ? '1 mois Premium gratuit pour chacun + 100 XP !'
                    : '1 free month of Premium for each + 100 XP!'
                  }
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}

function Step({ number, title, description }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">
        {number}
      </div>
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
